import React from 'react';
import { connect } from "react-redux";
import { ThunkDispatch } from "redux-thunk";
import { Action } from "redux";
import { DateType, DateRangeField } from 'app/pages/DiscountEditor/actionTypes';
import dayjs, { Dayjs } from 'dayjs';
import { Checkbox, Row, Col, Typography, theme, Divider, DatePicker } from 'antd';
import { getBookingDateRange } from 'app/pages/DiscountEditor/modules/DiscountDates';
import { setDateCommand, setNeverExpiresCommand } from 'app/pages/DiscountEditor/commands';
import { getProductDateRange } from 'app/pages/DiscountEditor/modules/DiscountDates/selectors';
import ComponentHeader from 'app/shared/ui/ComponentHeader';
import ErrorSummary from 'app/pages/DiscountEditor/components/ErrorSummary';
import { getSectionError } from 'app/pages/DiscountEditor/modules/Editor';
import { SaveDiscountErrorCategory } from 'app/shared/model/SaveDiscountResultDto';
import DateHelper from 'app/helpers/dateHelper';

const { useToken } = theme;
const { Text } = Typography;

type InternalProps = StateProps & DispatchProps;

interface StateProps {
    errorMessage?: string;
    bookingDateRange: DiscountDateRangeProps;
    productDateRange: DiscountDateRangeProps;
}

interface DispatchProps {
    bookingMethods: DiscountDateMethodProps;
    productMethods: DiscountDateMethodProps;
}

interface DiscountDateRangeProps {
    dateFrom: string;
    dateTo: string;
    neverExpires: boolean;
}

interface DiscountDateMethodProps {
    onNeverExpiresChanged: (neverExpires: boolean) => void;
    onDateFromChanged: (date: string) => void;
    onDateToChanged: (date: string) => void;
}

const DiscountDatesEditor: React.FC<InternalProps> = (props) => {
    const { token } = useToken();

    const renderDateRangeFields = (
        dateRange: DiscountDateRangeProps,
        methods: DiscountDateMethodProps
    ) => (
        <Row gutter={[16, 16]}>
            <Col span={dateRange.neverExpires ? 24 : 12}>
                <div style={{ marginBottom: token.margin }}>
                    <Text strong>From</Text>
                    <div style={{ width: '100%' }}>
                        <DatePicker
                            value={dateRange.dateFrom ? dayjs(dateRange.dateFrom) : null}
                            onChange={(date: Dayjs | null) =>
                                methods.onDateFromChanged(date ? date.format('YYYY-MM-DD') : '')
                            }
                            format={DateHelper.DATE_PICKER_FORMATS}
                            style={{ width: '100%' }}
                            allowClear={false}
                            placeholder="Select date"
                        />
                    </div>
                </div>
                <Checkbox
                    checked={dateRange.neverExpires}
                    onChange={(e: { target: { checked: boolean; }; }) => methods.onNeverExpiresChanged(e.target.checked)}
                >
                    Never expires
                </Checkbox>
            </Col>
            {!dateRange.neverExpires && (
                <Col span={12}>
                    <div style={{ marginBottom: token.margin }}>
                        <Text strong>To</Text>
                        <div style={{ width: '100%' }}>
                            <DatePicker
                                value={dateRange.dateTo ? dayjs(dateRange.dateTo) : null}
                                onChange={(date: Dayjs | null) =>
                                    methods.onDateToChanged(date ? date.format('YYYY-MM-DD') : '')
                                }
                                format={DateHelper.DATE_PICKER_FORMATS}
                                style={{ width: '100%' }}
                                allowClear={false}
                                placeholder="Select date"
                            />
                        </div>
                    </div>
                </Col>
            )}
        </Row>
    );

    return (
        <div style={{ marginBottom: 16, background: '#fff', border: '1px solid #f0f0f0', borderRadius: '4px' }}>
            <ComponentHeader text="Dates" />
        <div style={{ padding: '0 16px 16px' }}>
            <ErrorSummary errorMessage={props.errorMessage} />
            <Divider style={{ margin: '12px 0' }} />
                <Text style={{ display: 'block', marginBottom: token.marginLG }}>
                    Valid for bookings made...
                </Text>
                {renderDateRangeFields(props.bookingDateRange, props.bookingMethods)}
                <Divider />
                <Text style={{ display: 'block', margin: `${token.marginLG}px 0` }}>
                    Valid for products that intersect...
                </Text>
                {renderDateRangeFields(props.productDateRange, props.productMethods)}
            </div>
        </div>
    );
};

function calculateInitialDate(currentDate: string, otherDate: string): Date | undefined {
    if (currentDate === "" && otherDate === "") return undefined;
    const date = (currentDate === '' && otherDate !== '') ? otherDate : currentDate;
    //return dateFns.parseISO(date);
    return DateHelper.ToDate(date);
}

const mapStateToProps = (state: any): StateProps => {
    const bookingDateRange = getBookingDateRange(state);
    const productDateRange = getProductDateRange(state);

    return {
        errorMessage: getSectionError(state, SaveDiscountErrorCategory.Dates),
        bookingDateRange: {
            dateFrom: bookingDateRange.from,
            dateTo: bookingDateRange.to,
            neverExpires: bookingDateRange.neverExpires
        },
        productDateRange: {
            dateFrom: productDateRange.from,
            dateTo: productDateRange.to,
            neverExpires: productDateRange.neverExpires
        }
    };
};

const mapDispatchToProps = (dispatch: ThunkDispatch<any, any, Action>): DispatchProps => ({
    bookingMethods: {
        onNeverExpiresChanged: (neverExpires) => dispatch(setNeverExpiresCommand(DateType.Booking, neverExpires)),
        onDateFromChanged: (date) => dispatch(setDateCommand(DateType.Booking, DateRangeField.From, date)),
        onDateToChanged: (date) => dispatch(setDateCommand(DateType.Booking, DateRangeField.To, date))
    },
    productMethods: {
        onNeverExpiresChanged: (neverExpires) => dispatch(setNeverExpiresCommand(DateType.Product, neverExpires)),
        onDateFromChanged: (date) => dispatch(setDateCommand(DateType.Product, DateRangeField.From, date)),
        onDateToChanged: (date) => dispatch(setDateCommand(DateType.Product, DateRangeField.To, date))
    }
});

export default connect(mapStateToProps, mapDispatchToProps)(DiscountDatesEditor);
